import { X, ArrowUpDown, MoreHorizontalIcon } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  BlockEventIcon,
  BroadcastIcon,
  DeleteIcon,
  EditIcon,
  UserOctagonIcon,
} from '../Icons'

interface Event {
  id: string
  name: string
  organizer: string
  tags: string
  startDateTime: string
  endDateTime: string
  location: string
  status: 'Open' | 'Sold Out' | 'Closed'
}

interface ViewCategoryDrawerProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

// Sample events data based on the Figma design
const eventsData: Event[] = [
  {
    id: '1',
    name: 'Winter Skiing Championship 2024',
    organizer: 'Alpine Sports Inc.',
    tags: 'Beginner-friendly, Pro-level',
    startDateTime: '15 Dec 2025, 09:00',
    endDateTime: '17 Dec 2025, 18:00',
    location: 'Aspen, Colorado (US)',
    status: 'Open',
  },
  {
    id: '2',
    name: 'Sunrise Yoga & Wellness',
    organizer: 'Mountain Events',
    tags: 'Night Skiing, Freestyle',
    startDateTime: '20 Nov 2025, 06:30',
    endDateTime: '20 Nov 2025, 08:30',
    location: 'Central Park, NY (US)',
    status: 'Sold Out',
  },
  {
    id: '3',
    name: 'Tech Networking Night',
    organizer: 'Ski School Pro',
    tags: 'Family-friendly, Junior',
    startDateTime: '05 Jan 2026, 19:00',
    endDateTime: '05 Jan 2026, 22:00',
    location: 'San Francisco, CA (US)',
    status: 'Open',
  },
  {
    id: '4',
    name: 'Charity Food Festival',
    organizer: 'Adrenaline Events',
    tags: 'Weekend event, One-day event',
    startDateTime: '28 Dec 2025, 11:00',
    endDateTime: '28 Dec 2025, 20:00',
    location: 'Chicago, IL (US)',
    status: 'Closed',
  },
  {
    id: '5',
    name: 'Virtual Coding Workshop',
    organizer: 'Community Center',
    tags: 'Multi-day event, Morning session, Evening session',
    startDateTime: '02 Nov 2025, 10:00',
    endDateTime: '02 Nov 2025, 16:00',
    location: 'Online (Zoom)',
    status: 'Open',
  },
  {
    id: '6',
    name: 'Family Winter Carnival',
    organizer: 'Peak Performance Gear',
    tags: 'Late-night, Seasonal, Meditation',
    startDateTime: '10 Dec 2025, 10:00',
    endDateTime: '10 Dec 2025, 17:00',
    location: 'Denver, CO (US)',
    status: 'Open',
  },
  {
    id: '7',
    name: 'Startup Pitch Festival',
    organizer: 'Winter Wonderland Tours',
    tags: 'Acoustic sessions, Campfire night',
    startDateTime: '14 Nov 2025, 14:00',
    endDateTime: '14 Nov 2025, 19:00',
    location: 'Austin, TX (US)',
    status: 'Open',
  },
  {
    id: '8',
    name: 'Startup Pitch Festival',
    organizer: 'Snow Adventure Rentals',
    tags: 'Relax & unwind, Skiing',
    startDateTime: '22 Jan 2026, 08:30',
    endDateTime: '25 Dec 2025, 15:00',
    location: 'Seattle, WA (US)',
    status: 'Open',
  },
]

const getStatusBadgeProps = (status: string) => {
  switch (status) {
    case 'Open':
      return {
        className:
          'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100',
        children: 'Open',
      }
    case 'Sold Out':
      return {
        className: 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100',
        children: 'Sold Out',
      }
    case 'Closed':
      return {
        className:
          'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-200',
        children: 'Closed',
      }
    default:
      return {
        className:
          'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-200',
        children: status,
      }
  }
}

function ViewCategoryDrawer({ open, onOpenChange }: ViewCategoryDrawerProps) {
  const handleClose = () => {
    onOpenChange(false)
  }

  const handleOk = () => {
    onOpenChange(false)
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent
        aria-labelledby="event-details-title"
        aria-label="Event details drawer"
        className="h-full p-0 w-full max-w-max lg:max-w-[75%] rounded-none lg:rounded-xl"
      >
        {/* Header */}
        <DrawerHeader className="border-b border-ss-neutral-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-base md:text-lg font-semibold text-ss-neutral-950 pr-2">
              Events using competition category
            </DrawerTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 p-0 hover:bg-ss-neutral-50"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5 text-ss-neutral-700" />
            </Button>
          </div>
        </DrawerHeader>

        {/* Content */}
        <div className="flex-1 overflow-auto p-2 md:p-4">
          <div className="bg-white rounded-xl border border-ss-neutral-100 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-ss-neutral-25 hover:bg-ss-neutral-25">
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[176px]">
                      <div className="flex items-center gap-1">Event</div>
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[143px]">
                      Organizer
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[190px]">
                      Tags
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[159px]">
                      <div className="flex items-center gap-1">
                        Start Date & Time
                        <ArrowUpDown className="h-4 w-4 text-ss-neutral-600" />
                      </div>
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[160px]">
                      <div className="flex items-center gap-1">
                        End Date & Time
                        <ArrowUpDown className="h-4 w-4 text-ss-neutral-600" />
                      </div>
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[145px]">
                      Location
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 text-xs font-medium text-ss-neutral-950 min-w-[101px]">
                      Status
                    </TableHead>
                    <TableHead className="px-2 md:px-4 py-3 w-[52px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventsData.map(event => {
                    const badgeProps = getStatusBadgeProps(event.status)
                    return (
                      <TableRow
                        key={event.id}
                        className="border-b border-ss-neutral-100 hover:bg-ss-neutral-25/50"
                      >
                        <TableCell className="px-2 md:px-4 py-4">
                          <div className="flex flex-col gap-1 md:flex-row md:items-start md:gap-2">
                            <div className="text-sm font-semibold text-ss-neutral-950 leading-4.5  break-words">
                              {event.name}
                            </div>
                            {(event.id === '2' || event.id === '5') && (
                              <Badge className="bg-ss-blue-50 text-ss-blue-500 border-ss-blue-100 text-xs px-1.5 py-0 rounded-full w-fit">
                                Free
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5  break-words">
                            {event.organizer}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5  break-words">
                            {event.tags}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5  whitespace-nowrap">
                            {event.startDateTime}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5  whitespace-nowrap">
                            {event.endDateTime}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <div className="text-sm font-medium text-ss-neutral-600 leading-4.5  break-words">
                            {event.location}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <Badge
                            variant="outline"
                            className={cn(
                              'px-2.5 py-1 text-xs font-medium rounded-full',
                              badgeProps.className
                            )}
                          >
                            {badgeProps.children}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-2 md:px-4 py-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-8 w-8 p-0 hover:bg-ss-action-gray-50"
                                aria-label="Open event actions menu"
                                tabIndex={0}
                              >
                                <MoreHorizontalIcon
                                  className="h-5 w-5 text-ss-neutral-600"
                                  aria-hidden="true"
                                />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              aria-label="Event actions menu"
                            >
                              <DropdownMenuItem
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
                                aria-label={`Edit event ${event.name}`}
                                tabIndex={0}
                              >
                                <EditIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
                                // onClick={() => setSponsorDrawerOpen(true)}
                                aria-label={`Assign event admin for ${event.name}`}
                                tabIndex={0}
                              >
                                <UserOctagonIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                  strokeWidth="2"
                                />
                                Assign Event Admin
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
                                aria-label={`Delete event ${event.name}`}
                                tabIndex={0}
                              >
                                <BroadcastIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                                Assign Sponsor
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-neutral-600 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
                                aria-label={`Delete event ${event.name}`}
                                tabIndex={0}
                              >
                                <DeleteIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                  strokeWidth={1.4}
                                />
                                Delete
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-ss-critical-200 hover:bg-ss-action-gray-50 rounded-md cursor-pointer"
                                aria-label={`Suspend event ${event.name}`}
                                tabIndex={0}
                              >
                                <BlockEventIcon
                                  className="h-4 w-4"
                                  aria-hidden="true"
                                />
                                Suspend Event
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DrawerFooter className="flex-row justify-between items-center px-4 md:px-6 py-3 md:py-4 bg-white border-t border-ss-neutral-100 rounded-bl-none rounded-br-none lg:rounded-bl-xl lg:rounded-br-xl shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)]">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            className="h-9 px-3 md:px-4 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-25"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleOk}
            className="h-9 px-3 md:px-4 bg-ss-blue-900 text-white hover:bg-ss-blue-800"
          >
            OK
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default ViewCategoryDrawer
